import { useRef, useState, type ChangeEvent } from 'react';
import { Icon } from '../../components/Icon';
import { useAnnouncer } from '../../components/Status';
import { isVideo } from '../../domain/milestones';
import type { FamilyPhoto, MemorySubject } from '../../domain/types';
import { useI18n } from '../../i18n';
import { preparePhoto } from '../../lib/image';
import { uuid } from '../../lib/uuid';
import { useRepos } from '../../repositories';
import { useApp } from '../../app/AppState';
import { PhotoImage } from './MomentDialog';

/** Anything bigger than this is a phone's whole camera roll in one file; the prototype keeps it all in the browser. */
const MAX_FILE_BYTES = 80 * 1024 * 1024;

/**
 * *Add photos or videos* — the one control that makes a day on the timeline
 * something the family fills in rather than only reads.
 *
 * It is the same button on a simcha, on a story and on a birth, because it is
 * the same act: several files at once (that is how a phone hands over an
 * evening's pictures), photos downscaled like everywhere else, clips stored as
 * they came, since re-encoding video in the browser is not something this
 * prototype should attempt. Everything added takes the day's date and its
 * people from the repository (`MockPhotoRepository.anchorOf`).
 */
export function AddMemories({ subject, className = '', compact = false }: {
  subject: MemorySubject;
  className?: string;
  /** Beside a heading rather than across the page — for a section that is one row when empty. */
  compact?: boolean;
}) {
  const { t } = useI18n();
  const repos = useRepos();
  const { toast } = useAnnouncer();
  const { reload, session } = useApp();
  const [adding, setAdding] = useState<{ done: number; total: number } | null>(null);
  const pickRef = useRef<HTMLInputElement | null>(null);

  const onFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = '';
    if (!files.length) return;
    setAdding({ done: 0, total: files.length });
    let added = 0;
    let failed = 0;
    for (const [i, file] of files.entries()) {
      setAdding({ done: i, total: files.length });
      try {
        if (file.size > MAX_FILE_BYTES) {
          failed++;
          continue;
        }
        // Only a photograph is downscaled; a clip and a voice note are stored as they came.
        const picture = file.type.startsWith('image/');
        const prepared = picture ? await preparePhoto(file) : null;
        /*
         * Added to a day, it hangs off that day and takes its date and its
         * people. Added to a *person*, there is no day to hang off: it is an
         * ordinary picture with their face already tagged on it, which is what
         * makes it theirs and puts it on their ring.
         */
        const res = await repos.photos.add({
          image: prepared ? prepared.blob : file,
          width: prepared?.width,
          height: prepared?.height,
          isEvent: false,
          addedTo: subject.kind === 'person' ? undefined : subject,
          personIds: subject.kind === 'person' ? [subject.id] : undefined,
          idempotencyKey: uuid(),
        });
        if (res.ok) added++;
        else failed++;
      } catch {
        failed++;
      }
    }
    setAdding(null);
    if (added > 0) {
      toast(t('memories.added', { n: added }));
      reload();
    }
    if (failed > 0) toast(t('memories.addFailed', { n: failed }));
  };

  return (
    <>
      <input ref={pickRef} type="file" accept="image/*,video/*,audio/*" multiple hidden onChange={(e) => void onFiles(e)} />
      <button
        type="button"
        className={`btn ${compact ? 'btn-secondary btn-sm' : 'btn-primary btn-lg btn-block'} milestone-add ${className}`}
        onClick={() => pickRef.current?.click()}
        disabled={!session || !!adding}
        title={t('memories.add')}
      >
        <Icon name="plus" />
        <span>{adding ? t('memories.adding', { done: adding.done + 1, total: adding.total }) : compact ? t('memories.addShort') : t('memories.add')}</span>
      </button>
    </>
  );
}

/**
 * The pictures and clips inside one day, as a grid. A clip shows its first
 * frame with a play mark over it; whatever the family has already responded
 * with sits in the corner.
 */
export function MemoryTiles({ items, onOpen }: { items: FamilyPhoto[]; onOpen: (m: FamilyPhoto) => void }) {
  const { t } = useI18n();
  return (
    <div className="photo-grid">
      {items.map((m) => (
        <button
          key={m.id}
          type="button"
          className={`photo-tile ${isVideo(m) ? 'is-clip' : ''}`}
          onClick={() => onOpen(m)}
          aria-label={m.caption || (isVideo(m) ? t('memories.video') : t('photos.title'))}
        >
          {/* A grid of forty: the small square, not the original. */}
          <PhotoImage photo={m} thumb />
          {isVideo(m) && <span className="photo-clip-mark" aria-hidden="true"><Icon name="play" /></span>}
          {m.reactions && m.reactions.length > 0 && (
            <span className="photo-reactions" aria-hidden="true">{[...new Set(m.reactions.map((r) => r.emoji))].slice(0, 3).join('')}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/** Nothing added yet — said once, in the place where the button to fix it already is. */
export function MemoriesEmpty() {
  const { t } = useI18n();
  return (
    <div className="empty">
      <Icon name="image" />
      <p>{t('memories.empty')}</p>
      <p className="small muted">{t('memories.emptyHint')}</p>
    </div>
  );
}
